import type { AssetsIcons } from '@/shared/api';
import type {
  BankAddressField,
  BankAddressFlowHistoryItem,
  BankAddressStepParam,
  BankAddressStepResponse,
} from './types';
import { getRequiredLabel } from '@/shared/lib';
import { BANK_ADDRESS_PARAM_IDS } from './constants';

type Props = {
  step: BankAddressStepResponse | null;
  history: BankAddressFlowHistoryItem[];
  assetsIcons?: AssetsIcons;
};

export const getParamValue = (
  param: BankAddressStepParam,
  additionalParamValue?: string | number
) => {
  if (additionalParamValue) {
    return additionalParamValue;
  }

  if (Array.isArray(param.valueList)) {
    if (param.valueList.length === 1) {
      return param.valueList[0];
    } else if ('defaultIndex' in param) {
      const index = param.defaultIndex;

      if (typeof index === 'string') {
        return index;
      }

      if (typeof index === 'number') {
        return param.valueList[index];
      }

      return '';
    } else {
      return '';
    }
  }

  if ('defaultValue' in param) {
    return param.defaultValue;
  }

  return '';
};

const isEditableParam = (
  param: BankAddressStepParam,
  additionalParamValue?: string | number
) => {
  const hasValueList = Array.isArray(param.valueList);

  if (additionalParamValue) {
    return false;
  }

  return (
    'defaultValue' in param ||
    !hasValueList ||
    (hasValueList && param!.valueList!.length > 1)
  );
};

export const mapBankAddressStepToFields = ({
  step,
  history,
  assetsIcons,
}: Props): BankAddressField[] => {
  if (!assetsIcons || !step) return [];

  return step.params
    .map(param => {
      const additionalParamValue = step.values?.[param.paramId];
      const isCurrencyCode =
        param.paramId === BANK_ADDRESS_PARAM_IDS.currencyCode;
      const isSelect = Array.isArray(param.valueList);
      const isAddress = param.action === 'selectTemplates';

      const isRequired = param.isRequired ?? false;

      const placeholder = getRequiredLabel(param.paramName, isRequired);
      const options = param.valueList
        ? param.valueList.map(val => {
            return {
              value: val ?? step.values?.[param.paramId],
              code: val ?? step.values?.[param.paramId],
              name: val ?? step.values?.[param.paramId],
              icon: isCurrencyCode ? assetsIcons?.[val] : undefined,
            };
          })
        : [
            {
              value: additionalParamValue,
              code: additionalParamValue,
              name: additionalParamValue,
              icon: undefined,
            },
          ];

      const isEditable = isEditableParam(param, additionalParamValue);
      const isPrevStepParam = history.some(prev =>
        prev.fields.params.some(p => p.paramId === param.paramId)
      );

      return {
        id: param.paramId,
        placeholder,
        type: isSelect ? 'select' : isAddress ? 'address' : 'text',
        required: isRequired,
        visible: param.isVisible ?? param.isvivsible ?? true,
        disabled: !isEditable || isPrevStepParam,
        options,
        isPrevStepParam,
        defaultValue: getParamValue(param, additionalParamValue),
        valueDictionaryId: param?.valueDictionaryId,
        inputFormat: param?.inputFormat,
      };
    })
    .filter(Boolean) as BankAddressField[];
};
